import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BulkUpdateTasks, PostProjectTaskWorkstream } from '../../redux/taskSlice';
import { AddCircle24Filled } from '@fluentui/react-icons';
import { Menu, MenuTrigger, MenuPopover, MenuList, MenuDivider } from '@fluentui/react-components';
import { ChevronDown12Regular } from '@fluentui/react-icons';
import styles from './css/index.module.css';

const BulkWorkstreamUpdateMenu = ({ selectedRows, onBulkUpdateComplete, children, taskData }) => {
    const dispatch = useDispatch();
    const { projectWorkstreams } = useSelector(state => state.task);
    const { workstreams } = useSelector(state => state.dataroom);
    const [newWorkstreamName, setNewWorkstreamName] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [open, setOpen] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    // Get all workstreams from different sources (no duplicates)
    const getAllWorkstreams = () => {
        const workstreamMap = new Map();

        // 1. Project workstreams
        if (projectWorkstreams && projectWorkstreams.length > 0) {
            projectWorkstreams.forEach(ws => {
                workstreamMap.set(ws.name, { ...ws, source: 'project' });
            });
        }

        // 2. Dataroom workstreams
        if (workstreams && workstreams.length > 0) {
            workstreams.forEach(ws => {
                workstreamMap.set(ws.name, { ...ws, source: 'dataroom' });
            });
        }

        // 3. All workstreams from task data
        const getAllWorkstreamsFromTaskData = () => {
            // Handle both array and object structures
            let tasks = [];
            if (Array.isArray(taskData)) {
                tasks = taskData;
            } else if (taskData && taskData.data && Array.isArray(taskData.data)) {
                tasks = taskData.data;
            } else if (taskData && taskData.groups && Array.isArray(taskData.groups)) {
                // Handle grouped data - flatten all groups
                tasks = taskData.groups.flatMap(group => group.tasks || []);
            } else {
                return [];
            }

            return tasks
                .map(task => task.workstream_repr)
                .filter(workstream => workstream && workstream.trim() !== '')
                .filter((workstream, index, arr) => arr.indexOf(workstream) === index); // Remove duplicates
        };

        const taskWorkstreamNames = getAllWorkstreamsFromTaskData();
        taskWorkstreamNames.forEach(workstreamName => {
            if (!workstreamMap.has(workstreamName)) {
                workstreamMap.set(workstreamName, {
                    id: `task_${workstreamName}`,
                    name: workstreamName,
                    source: 'task',
                });
            }
        });

        return Array.from(workstreamMap.values());
    };

    const handleBulkWorkstreamSelect = async workstream => {
        if (!selectedRows || selectedRows.length === 0) return;

        setIsUpdating(true);
        try {
            // Update all selected tasks with the new workstream using bulk update
            await dispatch(
                BulkUpdateTasks({
                    taskIds: selectedRows,
                    updates: { workstream_repr: workstream.name },
                    suppressToast: true,
                }),
            );

            // Call the completion callback
            if (onBulkUpdateComplete) {
                onBulkUpdateComplete(workstream, selectedRows.length);
            }

            setOpen(false);
        } catch (error) {
            console.error('Error updating bulk task workstreams:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleAddNewWorkstream = async () => {
        if (!newWorkstreamName.trim()) return;

        setIsAdding(true);
        try {
            await dispatch(PostProjectTaskWorkstream({ name: newWorkstreamName.trim() }));
            setNewWorkstreamName('');
            setOpen(false);
        } catch (error) {
            console.error('Error adding new workstream:', error);
        } finally {
            setIsAdding(false);
        }
    };

    const allWorkstreams = getAllWorkstreams();

    return (
        <Menu open={open} onOpenChange={(e, data) => setOpen(data.open)}>
            <MenuTrigger>
                {children || (
                    <div className={styles.bulkUpdateMenu} style={{ opacity: isUpdating ? 0.6 : 1 }}>
                        Workstream <ChevronDown12Regular className="ms-1" />
                    </div>
                )}
            </MenuTrigger>
            <MenuPopover style={{ width: '280px', maxHeight: '400px', overflowY: 'auto', position: 'relative' }}>
                <MenuList>
                    {/* All Workstreams in one list */}
                    {allWorkstreams.length > 0 &&
                        allWorkstreams.map(workstream => (
                            <div
                                key={workstream.id}
                                onClick={() => handleBulkWorkstreamSelect(workstream)}
                                className={styles.tagMenuItem}
                                style={{ opacity: isUpdating ? 0.6 : 1, cursor: isUpdating ? 'not-allowed' : 'pointer' }}>
                                <span>{workstream.name}</span>
                            </div>
                        ))}

                    {/* Add New Workstream */}
                    <MenuDivider />
                    <div className="p-2">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <input
                                type="text"
                                placeholder="Add new workstream"
                                value={newWorkstreamName}
                                onChange={e => setNewWorkstreamName(e.target.value)}
                                onKeyPress={e => e.key === 'Enter' && handleAddNewWorkstream()}
                                className="form-control form-control-sm"
                                disabled={isAdding || isUpdating}
                            />
                            <button className="btn btn-sm btn-outline-primary" onClick={handleAddNewWorkstream} disabled={!newWorkstreamName.trim() || isAdding || isUpdating}>
                                <AddCircle24Filled />
                            </button>
                        </div>
                    </div>
                </MenuList>
            </MenuPopover>
        </Menu>
    );
};

export default BulkWorkstreamUpdateMenu;
